import { Link, useParams } from "react-router-dom";
import FanchaLayout from "@/components/feature/FanchaLayout";
import useSeo from "@/hooks/useSeo";
import { cases, CATEGORIES } from "@/mocks/fancha";
import ContrastBlock from "./components/ContrastBlock";
import TimelineBlock from "./components/TimelineBlock";
import TopicFaq from "./components/TopicFaq";

const catLabel = (key: string) => CATEGORIES.find((c) => c.key === key)?.label ?? "综合";

function RelatedCards({ currentId }: { currentId: string }) {
  const related = cases.filter((c) => c.id !== currentId).slice(0, 3);
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {related.map((c) => (
        <Link
          key={c.id}
          to={`/topic/${c.id}`}
          className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-shadow hover:shadow-lift"
        >
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={c.cover}
              alt={c.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
            />
          </div>
          <div className="flex flex-1 flex-col p-4">
            <span className="w-max rounded-full bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
              {catLabel(c.category)}
            </span>
            <h3 className="mt-2 flex-1 font-heading text-base font-bold leading-snug text-foreground-950">
              {c.title}
            </h3>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
              深扒档案 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function TopicPage() {
  const { id } = useParams<{ id: string }>();
  const item = cases.find((c) => c.id === id);

  useSeo({
    title: item
      ? `${item.title} - ${item.person}反差前后对比 | 反差吃瓜局`
      : "档案未找到 | 反差吃瓜局",
    description: item
      ? `${item.summary} 反差吃瓜局带你深扒${item.person}的人设反转、身份揭秘与前后对比，还原${item.title}的完整来龙去脉。`
      : "没有找到对应的反差档案。",
    keywords: `反差吃瓜,反差吃瓜人设反转,${item ? item.person : ""},反差吃瓜前后对比`,
    canonicalPath: `/topic/${id}`,
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            articleSection: catLabel(item.category),
            datePublished: item.published,
            author: { "@type": "Organization", name: "反差吃瓜局" },
            articleBody: item.paragraphs.join(" "),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "反转档案", item: "/#cases" },
              { "@type": "ListItem", position: 3, name: item.person },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: item.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <FanchaLayout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-folder-forbid-line text-5xl text-foreground-400" />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">没有找到这份档案</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已被归档或地址有误，去「反转档案」看看吧。</p>
          <Link
            to="/#cases"
            className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回反转档案
          </Link>
        </div>
      </FanchaLayout>
    );
  }

  return (
    <FanchaLayout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="whitespace-nowrap hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/#cases" className="whitespace-nowrap hover:text-primary-700">反转档案</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="whitespace-nowrap font-medium text-foreground-800">{item.person}</span>
        </nav>

        <article>
          {/* 头部 */}
          <header className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                {item.tag}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                {catLabel(item.category)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-fire-fill text-primary-500" /> 热度 {item.hot}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-4 font-heading text-lg leading-8 text-foreground-700">{item.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/70 py-4 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground-950 text-background-50">
                  <i className="ri-user-3-line" />
                </span>
                <span className="whitespace-nowrap font-medium text-foreground-800">深扒记者 · {item.person}</span>
              </span>
              <time dateTime={item.published} className="whitespace-nowrap inline-flex items-center gap-1">
                <i className="ri-time-line" /> {item.published} 发布
              </time>
              <span className="whitespace-nowrap inline-flex items-center gap-1"><i className="ri-eye-line" /> {item.views} 浏览</span>
              <span className="whitespace-nowrap inline-flex items-center gap-1"><i className="ri-chat-1-line" /> {item.comments} 评论</span>
            </div>
          </header>

          {/* 主视觉 */}
          <div className="mt-8 aspect-[16/8] overflow-hidden rounded-3xl md:aspect-[16/6]">
            <img src={item.cover} alt={`${item.person}：${item.title} 前后对比插画`} className="h-full w-full object-cover" />
          </div>

          {/* 前后对比 */}
          <section className="mt-12" aria-labelledby="fc-contrast-title">
            <h2 id="fc-contrast-title" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              当事人前后对比
            </h2>
            <p className="mt-2 mb-6 max-w-2xl text-sm text-foreground-600">
              同一副面孔，两种面孔。我们把「台前的完美设定」与「台后的反转实锤」并排摆开，反差一目了然。
            </p>
            <ContrastBlock before={item.before} after={item.after} />
          </section>

          {/* 正文 + 时间线 */}
          <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-black text-foreground-950">深扒正文</h2>
              <div className="prose-fc mt-4">
                {item.paragraphs.map((p) => (
                  <p key={p}>{p}</p>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {item.watch.map((w) => (
                  <span key={w} className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-800">
                    {w}
                  </span>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-2xl border border-background-200/70 bg-background-100/60 p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">TIMELINE</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950">完整时间线</h2>
                <div className="mt-6">
                  <TimelineBlock points={item.timeline} />
                </div>
                <div className="mt-6 rounded-xl bg-foreground-950 p-4 text-sm text-background-100">
                  <p className="flex items-center gap-2 font-semibold text-accent-300">
                    <i className="ri-flashlight-line" /> 小结
                  </p>
                  <p className="mt-2 leading-6 text-background-200/85">
                    人设越完美，容错越低。当公开形象与现实的距离被一次次拉大，翻车就只是时间问题。
                  </p>
                </div>
              </div>
            </aside>
          </div>

          {/* 专题FAQ */}
          <section className="mt-14" aria-labelledby="fc-faq-title">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">DISCUSSION</p>
              <h2 id="fc-faq-title" className="mt-1 font-heading text-2xl font-black text-foreground-950">
                关于这起反转的延伸讨论
              </h2>
            </div>
            <TopicFaq faq={item.faq} />
          </section>
        </article>

        {/* 相关档案 */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-black text-foreground-950">更多反转档案</h2>
          <div className="mt-6">
            <RelatedCards currentId={item.id} />
          </div>
        </section>

        {/* CTA */}
        <div className="mt-12 flex flex-col items-center justify-between gap-5 rounded-3xl bg-primary-100 px-6 py-8 text-center md:flex-row md:px-10 md:text-left">
          <div>
            <h2 className="font-heading text-xl font-bold text-primary-900">你有新的反转线索吗？</h2>
            <p className="mt-1 text-sm text-foreground-700">把「光鲜背后」的真相交给我们深扒，理性吃瓜，一起讲证据。</p>
          </div>
          <Link
            to="/#contribute"
            className="whitespace-nowrap shrink-0 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-700"
          >
            提交线索
          </Link>
        </div>
      </div>
    </FanchaLayout>
  );
}